import { MembershipTransaction } from '@models/membership/MembershipTransaction'
import { membershipTransactionSchema } from '@models/schema'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchMembershipTransactions } from '@services/membership/MembershipTransactionService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  SBAPI,
  SBSelectRaw,
  SBStateUUID,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

// UUID-keyed state: payments are identified by `subscription_payments.id`, so
// ids and `selectedId` are strings.
const initialState: SBStateUUID<MembershipTransaction> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'membershipTransaction',
  initialState,
  reducers: {
    ...baseReducers,
    // Adds rows to `items` WITHOUT touching `ids`, `query` or `isLoading`: the
    // Revenue page's preview keeps its own rows, but its "View" action reads
    // the row from here, and must not disturb the drill-down's table state.
    upsertItems: (state, action: PayloadAction<MembershipTransaction[]>) => {
      action.payload.forEach((row) => {
        state.items[row.id] = row
      })
    },
  },
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getMembershipTransactionsSuccess,
  setQuery: setMembershipTransactionQuery,
  reset: resetMembershipTransactionState,
  resetQueryAndIds: resetMembershipTransactionQueryAndIds,
  upsertItems: upsertMembershipTransactions,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: string]: MembershipTransaction }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, string[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, string | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMembershipTransactions = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => (ids ?? []).map((id) => items[id]).filter((i) => i)
    // Guard against an un-hydrated/reset slice (ids undefined); filter keeps
    // only non-null elements.
  )
/**
 * The SBRM "View" (ticket 537, comment #13) reads the transaction straight
 * from this slice's rows - no dedicated fetch-by-id endpoint needed since the
 * row is already loaded by the table/preview that opened the drawer.
 */
export const selectSelectedMembershipTransaction = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )

/**
 * Row by id, used by the "View" content to resolve the transaction from SBRM's
 * `entityId` URL param.
 *
 * Needed because SBRM only calls `setSelected` for an id that is an integer, a
 * ULID, or a UUID **v1-v5** (`isUuid` in `@supplement-bacon/sbrm-react`), while
 * payments are keyed with a UUID **v7** (Laravel `HasUuids`). Without this the
 * selection is never set and the drawer stays on its spinner.
 */
export const selectMembershipTransactionById = (id?: string | null) =>
  createSelector([selectRawItems], (items) => (id ? items[id] : undefined))

/**
 * Actions
 */

export const setSelectedMembershipTransaction =
  (transaction: MembershipTransaction | string) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      // Not `getIdOrModelId`: it coerces with `Number()`, which turns a UUID
      // into `NaN` - the drawer would then never find its row.
      slice.actions.setSelectedId(
        typeof transaction === 'string' ? transaction : transaction.id
      )
    )

export const getMembershipTransactions = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchMembershipTransactions(params),
    // Array schema: the endpoint returns a list, and normalizing a list against
    // a bare entity yields `result: undefined` (so no ids, so an empty table).
    [membershipTransactionSchema],
    slice.actions
  )
